import { useQuery } from "@apollo/client";
import { useAuth } from "providers/AuthProvider";
import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useMemo } from "react";
import { gql } from "types";

export const features = {
  SUBSCRIPTIONS_FEATURE: "subscriptions_feature",
  SAML_FEATURE: "saml_feature",
  AVS_FEATURE: "avs_feature",
  IPP_FEATURE: "ipp_feature",
  BENEFIT_ASSIGNMENT: "benefit_assignment",
  WEB_CHART_FEATURE: "web_chart_feature",
  BENEFICIARIES_FEATURE: "beneficiaries_feature",
  ADJUSTMENTS_FEATURE: "adjustments_feature",
  TASKS_FEATURE: "tasks_feature",
  MARKET_ORDERS_CONFIRM_UPLOAD_FEATURE: "market_orders_confirm_feature",
  STATEMENTS_FEATURE: "statements_feature",
  COMPANY_HOLDINGS_FEATURE: "company_holdings_feature",
  DATA_EDITOR_FEATURE: "data_editor_feature",
} as const;

export type Feature = (typeof features)[keyof typeof features];

type FeaturesContextType = {
  isEnabled: (featureName: Feature) => boolean;
  refetchFeatures: () => void;
};

export const FeaturesContext = createContext<FeaturesContextType>({
  isEnabled: () => false,
  refetchFeatures: () => {},
});

const FEATURES_QUERY = gql(/* GraphQL */ `
  query FeaturesQuery {
    featuresList
  }
`);

type Props = {
  children: ReactNode;
};

export const FeaturesProvider = ({ children }: Props) => {
  const { authState } = useAuth();
  const { data, refetch } = useQuery(FEATURES_QUERY, {
    skip: !authState?.accessToken,
  });

  const featuresList = data?.featuresList ?? [];

  const isEnabled = useCallback(
    (featureName: Feature): boolean => {
      return featuresList.includes(featureName);
    },
    [featuresList],
  );

  const contextValue = useMemo(() => {
    return { isEnabled, refetchFeatures: refetch };
  }, [isEnabled, refetch]);

  return (
    <FeaturesContext.Provider value={contextValue}>
      {children}
    </FeaturesContext.Provider>
  );
};

export const useFeature = (featureName: Feature): boolean => {
  const context = useContext(FeaturesContext);
  if (!context)
    throw new Error("useFeature must be used within a FeaturesProvider");

  return context.isEnabled(featureName);
};
